import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  Input,
  ChangeDetectorRef
} from '@angular/core';
import {
  DialogContentBase,
  DialogRef,
  DialogService
} from '@progress/kendo-angular-dialog';
import { RemoveEvent } from '@progress/kendo-angular-grid';
import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { RoleTypeData } from '@module-role/interfaces/role-type-data.interface';
import { ENUM_ROLES } from '@module-role/enums/enum-roles';
import { IAsyncData } from '@module-claims/store/claims.state';
import { ClaimsService } from '@module-claims/services/claims.service';
import { PharmacyDetails } from '@module-claims/models/pharmacy.interface';
import {
  ContractPharmacyModalService,
  InhousePharmacyModalService
} from '@module-claims/services/pharmacy-modal.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-pharmacy-details-modal',
  templateUrl: './pharmacy-details-modal.component.html',
  styleUrls: ['./pharmacy-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PharmacyDetailsModalComponent
  extends DialogContentBase
  implements OnInit, OnDestroy
{
  @Input() npi?: string;

  externalLinkRoles: RoleTypeData = {
    roles: [
      ENUM_ROLES.RXP_ADMIN,
      ENUM_ROLES.RXP_EXECUTIVE,
      ENUM_ROLES.RXP_STAFF,
      ENUM_ROLES.CUSTOMER_ADMIN,
      ENUM_ROLES.CUSTOMER_EXECUTIVE,
      ENUM_ROLES.CUSTOMER_STAFF,
      ENUM_ROLES.CUSTOMER_REPORTER
    ]
  };

  editRoles: RoleTypeData = {
    roles: [
      ENUM_ROLES.RXP_ADMIN,
      ENUM_ROLES.RXP_EXECUTIVE,
      ENUM_ROLES.RXP_STAFF
    ]
  };

  pharmacyDetails$: Observable<IAsyncData<PharmacyDetails>>;
  npiSearch?: string;
  form!: UntypedFormGroup;
  dateTimeFormat = 'M/d/yyyy h:mm a';
  lastSearchDate?: Date;
  searchSubmitted = false;

  readonly destroy$ = new Subject();

  constructor(
    private _dialogRef: DialogRef,
    private _claimsService: ClaimsService,
    private _dialogService: DialogService,
    private _contractPharmacyModalService: ContractPharmacyModalService,
    private _inhousePharmacyModalService: InhousePharmacyModalService,
    private _fb: UntypedFormBuilder,
    private _cdr: ChangeDetectorRef
  ) {
    super(_dialogRef);
    this.pharmacyDetails$ = this._claimsService.pharmacyDetails$;
  }

  ngOnInit(): void {
    this._claimsService.clearPharmacyDetails();

    this.form = this._fb.group({
      npi: [this.npi]
    });

    this.getPharmacyDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  getPharmacyDetails(): void {
    const npi = this.form.get('npi')?.value?.trim();

    if (npi) {
      this.searchSubmitted = false;
      this.npiSearch = npi;
      this._claimsService.clearPharmacyDetails();
      this._claimsService.getPharmacyDetails(npi).subscribe(() => {
        this.searchSubmitted = true;
        this.lastSearchDate = new Date();
        this._cdr.detectChanges();
      });
    }
  }

  addContractPharmacy(): void {
    const npi = this.npiSearch;

    if (!npi) {
      return;
    }

    const sub$ = this._contractPharmacyModalService
      .open(npi)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data) => {
          if (data.action === 'save') {
            return this._claimsService.linkContractPharmacy(data.data!).pipe(
              map(() => ({ data })),
              catchError(() => {
                return of({ data: null });
              })
            );
          }
          return of({ data });
        }),
        filter(({ data }) => !!data)
      )
      .subscribe(({ data }) => {
        if (data?.action === 'save') {
          this._claimsService.getPharmacyDetails(npi);
        }
        sub$.unsubscribe();
        data?.modalRef.close();
      });
  }

  removeContractPharmacy(event: RemoveEvent): void {
    const npi = this.npiSearch;

    if (!npi) {
      return;
    }

    const ref = this.showConfirm(
      'Unlink HRSA Pharmacy',
      'Do you wish to unlink this HRSA pharmacy?'
    );
    ref.result
      .pipe(
        filter((result: any) => result.text === 'Yes'),
        takeUntil(this.destroy$),
        switchMap(() =>
          this._claimsService.unlinkContractPharmacy(npi, event.dataItem)
        )
      )
      .subscribe(() => {
        this._claimsService.getPharmacyDetails(npi);
      });
  }

  addInhousePharmacy(): void {
    const npi = this.npiSearch;

    if (!npi) {
      return;
    }

    const sub$ = this._inhousePharmacyModalService
      .open(npi)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data) => {
          if (data.action === 'save') {
            return this._claimsService.linkInhousePharmacy(data.data!).pipe(
              map(() => ({ data })),
              catchError(() => {
                return of({ data: null });
              })
            );
          }
          return of({ data });
        }),
        filter(({ data }) => !!data)
      )
      .subscribe(({ data }) => {
        if (data?.action === 'save') {
          this._claimsService.getPharmacyDetails(npi);
        }
        sub$.unsubscribe();
        data?.modalRef.close();
      });
  }

  removeInhousePharmacy(event: RemoveEvent): void {
    const npi = this.npiSearch;

    if (!npi) {
      return;
    }

    const ref = this.showConfirm(
      'Unlink In-House Pharmacy',
      'Do you wish to unlink this In-House pharmacy?'
    );
    ref.result
      .pipe(
        filter((result: any) => result.text === 'Yes'),
        takeUntil(this.destroy$),
        switchMap(() =>
          this._claimsService.unlinkInhousePharmacy(npi, event.dataItem)
        )
      )
      .subscribe(() => {
        this._claimsService.getPharmacyDetails(npi);
      });
  }

  private showConfirm(title: string, content: string): DialogRef {
    return this._dialogService.open({
      title,
      content,
      actions: [{ text: 'No' }, { text: 'Yes', primary: true }]
    });
  }
}
